import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import { FieldRadio, Legend } from 'cstar-react-primitives';

import { useFormatMessage } from '../../intl/intlHooks';

import { isEdge } from '../../utils/browser';
import { debounce } from '../../utils/sharedFunctions';

import { FaceplateImg } from './FaceplateImg';
import { faceplateMessages } from './faceplateMessages';

const CAROUSEL_OPTIONS_DISPLAYED = 3;
const SCROLL_ENDS_FUDGE_FACTOR = 5;
const SCROLL_DEBOUNCE_MS = 75;
const RESIZE_DEBOUNCE_MS = 300;

export const FaceplateCarousel = ({ faceplates, selected, handleChange }) => {
    const formatMessage = useFormatMessage();
    const carousel = useRef();
    const [isAtEnd, setIsAtEnd] = useState(false);
    const [itemWidth, setItemWidth] = useState(0);
    const [isAtStart, setIsAtStart] = useState(false);
    const [carouselListWidth, setCarouselListWidth] = useState(0);
    const [carouselScrollEnd, setCarouselScrollEnd] = useState(0);

        const scrollToSelected = () => {
        const selectedIndex = faceplates.findIndex(faceplate => selected.code === faceplate.code);
        const previousCount = (CAROUSEL_OPTIONS_DISPLAYED - 1) / 2;
        const scrollFn = () => {
            carousel.current.scrollLeft = Math.floor(itemWidth) * (selectedIndex - previousCount);
        };
        if (!isEdge()) {
            scrollFn();
        } else {
            
            
            
            setTimeout(scrollFn, 1);
        }
    };
        const handleFocus = evt => {
        
        
        if (evt.relatedTarget !== null && !evt.currentTarget.contains(evt.relatedTarget)) {
            scrollToSelected();
        }
    };

        const handleScroll = () => {
        setIsAtStart(carousel.current.scrollLeft < SCROLL_ENDS_FUDGE_FACTOR);
        setIsAtEnd(carousel.current.scrollLeft > carouselScrollEnd - SCROLL_ENDS_FUDGE_FACTOR);
    };

        const moveCarousel = direction => {
        carousel.current.scrollLeft += carouselListWidth * direction;
    };

        const calculateListBounds = () => {
        const carouselWidth = carousel.current.getBoundingClientRect().width;
        const list = carousel.current.children[1];
        setCarouselListWidth(list.getBoundingClientRect().width);

        const firstItem = list.children[0];
        if (firstItem) {
            const listOffset = firstItem.offsetLeft;
            setItemWidth(firstItem.getBoundingClientRect().width);
            const listWidth = listOffset * 2 + Math.floor(itemWidth) * faceplates.length;
            setCarouselScrollEnd(listWidth - carouselWidth);
        } else {
            
            setItemWidth(0);
            setCarouselScrollEnd(0);
        }
    };
        const handleWindowResize = () => {
        if (carousel) {
            calculateListBounds();
            handleScroll();
        }
    };
    const debouncedResizeHandler = () => debounce(handleWindowResize, RESIZE_DEBOUNCE_MS);

    useEffect(() => {
        if (selected) {
            handleChange(selected.code);
        }
        window.addEventListener('resize', debouncedResizeHandler);
        return () => window.removeEventListener('resize', debouncedResizeHandler);
    }, []);

    useEffect(() => {
        if (carousel && selected) {
            calculateListBounds();
            scrollToSelected();
            handleScroll();
        }
    }, [selected]);
    return (
        <div
            className={classNames('faceplate-carousel', {
                'is-at-start': isAtStart,
                'is-at-end': isAtEnd
            })}
        >
            <div className="faceplate-carousel-controls">
                <button
                    type="button"
                    className="faceplate-carousel-control back"
                    onClick={() => {
                        moveCarousel(-1);
                    }}
                    aria-hidden={isAtStart}
                    tabIndex={isAtStart ? -1 : 0}
                >
                    <div className="inner" />
                    <FormattedMessage {...faceplateMessages.backButton} />
                </button>
                <button
                    type="button"
                    className="faceplate-carousel-control forward"
                    onClick={() => {
                        moveCarousel(1);
                    }}
                    aria-hidden={isAtEnd}
                    tabIndex={isAtEnd ? -1 : 0}
                >
                    <div className="inner" />
                    <FormattedMessage {...faceplateMessages.forwardButton} />
                </button>
            </div>
            <fieldset
                className="faceplate-carousel-options"
                ref={carousel}
                onScroll={debounce(handleScroll, SCROLL_DEBOUNCE_MS)}
                onFocus={handleFocus}
            >
                <Legend message={formatMessage(faceplateMessages.carouselLabel)} />
                <ul>
                    {faceplates.map(faceplate => (
                        <li
                            key={faceplate.code}
                            className={classNames('faceplate-carousel-option', {
                                'is-selected': selected.code === faceplate.code
                            })}
                        >
                            <div className="faceplate-carousel-faceplate-container">
                                <FieldRadio
                                    name="faceplate"
                                    val={faceplate.code}
                                    isChecked={selected.code === faceplate.code}
                                    handleChange={handleChange}
                                >
                                    <FaceplateImg faceplate={faceplate} />
                                </FieldRadio>
                            </div>
                        </li>
                    ))}
                </ul>
            </fieldset>
        </div>
    );
};

FaceplateCarousel.propTypes = {
    faceplates: PropTypes.array.isRequired,
    selected: PropTypes.object.isRequired,
    handleChange: PropTypes.func.isRequired
};
